import React, { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAsyncData } from "@/hooks/useAsyncData";
import { LoadErrorState, LoadingState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Trash2, Skull, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { CountsTable, rangeText, dateParam, rangeProblem } from "./CountsTable";

export default function WipeTab() {
  const { today, refreshBoot } = useApp();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [everything, setEverything] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const problem = rangeProblem(from, to);
  const qFrom = problem ? undefined : dateParam(from);
  const qTo = problem ? undefined : dateParam(to);
  // keepPrevious: counts refresh in place — the checkbox tables never unmount while you interact
  const stats = useAsyncData(
    () => api.get("/backup/stats", { params: { date_from: qFrom, date_to: qTo } }).then((r) => r.data),
    [qFrom, qTo],
    { keepPrevious: true }
  );
  const allKeys = stats.data ? [...Object.keys(stats.data.dated), ...Object.keys(stats.data.masters)] : [];

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
    setEverything(false);
  };
  const toggleEverything = (on) => {
    setEverything(on);
    setSelected(on ? new Set(allKeys) : new Set());
    if (on) { setFrom(""); setTo(""); }
  };

  const wipe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/backup/wipe", {
        collections: [...selected], date_from: from || null, date_to: to || null, confirm_text: confirm,
      });
      setResult(data); setConfirm(""); setSelected(new Set()); setEverything(false);
      toast.success("Data cleared");
      stats.refresh();
      refreshBoot();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const total = stats.data ? [...selected].reduce((a, k) => a + (stats.data.counts[k] || 0), 0) : 0;
  const canWipe = !busy && confirm === "DELETE" && selected.size > 0 && !problem;

  return (
    <div className="space-y-4" data-testid="wipe-tab">
      <Card className="rounded-lg border-[hsl(var(--danger))]/40"><CardContent className="p-4 space-y-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded bg-[hsl(var(--danger))]/15 flex items-center justify-center shrink-0"><Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" /></div>
          <div>
            <h2 className="text-base md:text-lg font-semibold">Clear data</h2>
            <p className="text-sm text-muted-foreground">Pick a date range (leave blank for all time) and tick what to delete. Dated items respect the range; master items are always cleared entirely. Take an encrypted backup first — this cannot be undone.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5"><Label>From</Label><Input type="date" value={from} max={to || today} onChange={(e) => { setFrom(e.target.value); setEverything(false); }} disabled={everything} className="h-10 w-[165px]" data-testid="wipe-from" /></div>
          <div className="space-y-1.5"><Label>To</Label><Input type="date" value={to} min={from} max={today} onChange={(e) => { setTo(e.target.value); setEverything(false); }} disabled={everything} className="h-10 w-[165px]" data-testid="wipe-to" /></div>
          <span className="text-xs text-muted-foreground pb-2.5 flex items-center gap-1.5">
            Range: <b className="text-foreground">{rangeText(from, to)}</b>
            {stats.pending && stats.data && <RefreshCw className="h-3 w-3 animate-spin" data-testid="wipe-counts-updating" />}
          </span>
          {problem && <span className="text-xs text-[hsl(var(--warning))] pb-2.5" data-testid="wipe-range-problem">{problem}</span>}
        </div>

        <label className={`flex items-center gap-3 rounded border p-3 cursor-pointer transition-colors duration-150 ${everything ? "border-[hsl(var(--danger))] bg-[hsl(var(--danger))]/10" : "hover:border-[hsl(var(--danger))]/60"}`} data-testid="wipe-everything-label">
          <input type="checkbox" checked={everything} onChange={(e) => toggleEverything(e.target.checked)} className="h-4 w-4 accent-[hsl(var(--ruby))]" data-testid="wipe-everything-checkbox" />
          <Skull className="h-4 w-4 text-[hsl(var(--danger))]" />
          <span className="text-sm"><b>Everything — wipe the software clean and leave no trace</b> <span className="text-muted-foreground">(all dates, every item below, including the audit log; only your admin login is kept)</span></span>
        </label>

        {stats.error && !stats.data ? <LoadErrorState error={stats.error} onRetry={stats.reload} title="Could not count records" />
          : !stats.data ? <LoadingState />
          : (
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1.5">Daily records (follow the date range)</p>
                <CountsTable counts={stats.data.counts} labels={stats.data.dated} selected={selected} onToggle={toggle} rangeLabel={rangeText(from, to)} testId="wipe-dated" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1.5">Master data (always entire)</p>
                <CountsTable counts={stats.data.counts} labels={stats.data.masters} selected={selected} onToggle={toggle} rangeLabel={from || to ? "audit log follows range" : ""} testId="wipe-masters" />
              </div>
            </div>
          )}
        {stats.error && stats.data && <p className="text-xs text-[hsl(var(--danger))]" data-testid="wipe-counts-error">Counts could not be refreshed: {errMsg(stats.error)}</p>}

        <div className="flex flex-wrap items-end gap-3 pt-1">
          <div className="space-y-1.5">
            <Label>Type <span className="font-mono-num text-[hsl(var(--danger))]">DELETE</span> to confirm</Label>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value.toUpperCase())} placeholder="DELETE" className="h-10 w-[165px] font-mono-num" data-testid="wipe-confirm-input" />
          </div>
          <Button onClick={wipe} disabled={!canWipe}
            className="h-10 bg-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/90 text-white" data-testid="wipe-button">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />{busy ? "Clearing…" : `Delete ${selected.size} item${selected.size === 1 ? "" : "s"} (${total} records)`}
          </Button>
          {!canWipe && !busy && (
            <span className="text-xs text-muted-foreground pb-2.5" data-testid="wipe-hint">
              {selected.size === 0 ? "Tick at least one item" : problem ? problem : confirm !== "DELETE" ? "Type DELETE to enable" : ""}
            </span>
          )}
        </div>
      </CardContent></Card>

      {result && (
        <Alert className="border-[hsl(var(--danger))]/50 bg-[hsl(var(--danger))]/10" data-testid="wipe-result">
          <AlertTriangle className="h-4 w-4 text-[hsl(var(--danger))]" />
          <AlertTitle>Cleared</AlertTitle>
          <AlertDescription className="text-sm">
            {Object.entries(result.deleted).map(([k, n]) => `${k}: ${n}`).join(" · ")}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
